import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/lib/utils';
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from '@/components/ui/hover-card';

interface QuickStats {
  sessions: number;
  totalNet: number;
  winRate: number;
  bestNet: number;
}

interface PlayerQuickStatsCardProps {
  playerId: string;
  playerName: string;
  children: React.ReactNode;
}

export function PlayerQuickStatsCard({ playerId, playerName, children }: PlayerQuickStatsCardProps) {
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [fetched, setFetched] = useState(false);

  const fetchStats = async () => {
    if (fetched) return;
    setFetched(true); // mark immediately so rapid mouse-ins don't double-fetch

    const { data } = await supabase
      .from('session_players')
      .select('net_amount, sessions!inner(status)')
      .eq('player_id', playerId)
      .neq('sessions.status', 'active');

    if (!data || data.length === 0) {
      setStats({ sessions: 0, totalNet: 0, winRate: 0, bestNet: 0 });
      return;
    }

    const nets = data.map(d => d.net_amount || 0);
    const totalNet = nets.reduce((s, n) => s + n, 0);
    const wins = nets.filter(n => n > 0).length;
    const bestNet = Math.max(...nets);

    setStats({
      sessions: data.length,
      totalNet,
      winRate: Math.round((wins / data.length) * 100),
      bestNet,
    });
  };

  return (
    <HoverCard openDelay={300}>
      <HoverCardTrigger asChild onMouseEnter={fetchStats}>
        {children as React.ReactElement}
      </HoverCardTrigger>
      <HoverCardContent className="w-52 p-3" side="top">
        {stats ? (
          <div className="space-y-1.5">
            <p className="text-xs font-bold">{playerName}</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <span className="text-muted-foreground">Sessions</span>
              <span className="font-mono font-semibold text-right">{stats.sessions}</span>
              <span className="text-muted-foreground">All-time net</span>
              <span className={`font-mono font-semibold text-right ${stats.totalNet >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {stats.totalNet >= 0 ? '+' : ''}{formatCurrency(stats.totalNet)}
              </span>
              <span className="text-muted-foreground">Win rate</span>
              <span className="font-mono font-semibold text-right">{stats.winRate}%</span>
              <span className="text-muted-foreground">Best session</span>
              <span className="font-mono font-semibold text-right text-green-600">+{formatCurrency(stats.bestNet)}</span>
            </div>
          </div>
        ) : null}
      </HoverCardContent>
    </HoverCard>
  );
}
